import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { CricketMatchPredictionFetch } from "@services/CricketPrediction";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@components/ui/spinner";
import React from "react";
import { X } from "lucide-react";

type UserPointsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  userData: {
    user_id: number;
    full_name: string;
    display_name: string;
    email: string;
    phone_number: string;
    total_score: number;
  };
};

const UserPointsModal: React.FC<UserPointsModalProps> = ({
  isOpen,
  onClose,
  userData,
}) => {
  const { data: predictions, isLoading } = useQuery({
    queryKey: ["user-predictions", userData.user_id],
    queryFn: () =>
      CricketMatchPredictionFetch({ cricket_user: userData.user_id }),
    enabled: isOpen,
  });

  const predictionList = predictions?.data?.results || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="min-w-[720px] max-w-4xl max-h-[90vh] overflow-y-auto p-0 border-none bg-[#F8F9FA] [&>button]:hidden">
        <div className="relative p-6">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 rounded-full bg-white shadow-sm"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>

          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold text-gray-800">
              User Points
            </DialogTitle>
            <p className="text-sm text-gray-500">
              See all the points user has earned from correct predictions
            </p>
          </DialogHeader>

          {/* User Profile Summary */}
          <div className="flex justify-between items-start bg-white p-6 rounded-xl shadow-sm mb-6">
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-gray-900">
                {userData.full_name}
              </h3>
              <p className="text-sm text-gray-600">{userData.display_name}</p>
              <p className="text-sm text-gray-500">{userData.email}</p>
              <p className="text-sm text-gray-500">{userData.phone_number}</p>
            </div>
            <div className="bg-[#E6F6F1] p-4 rounded-xl text-center min-w-[120px]">
              <p className="text-xs font-semibold text-[#1DA077] uppercase tracking-wider mb-1">
                Total Points
              </p>
              <p className="text-3xl font-bold text-[#1DA077]">
                {userData.total_score}
              </p>
            </div>
          </div>

          {/* Table Container */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="grid grid-cols-6 bg-[#E6F6F1] p-4 text-xs font-bold text-[#1DA077] border-b border-[#D1EDE4]">
              <div className="flex items-center justify-center border-r border-[#D1EDE4]">
                Match_ID
              </div>
              <div className="flex items-center justify-center border-r border-[#D1EDE4]">
                Display Name
              </div>
              <div className="flex items-center justify-center border-r border-[#D1EDE4]">
                Predicted Toss Winner
              </div>
              <div className="flex items-center justify-center border-r border-[#D1EDE4]">
                Predicted Match Winner
              </div>
              <div className="flex items-center justify-center border-r border-[#D1EDE4]">
                Player of Match
              </div>
              <div className="flex items-center justify-center">
                Total Points
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {isLoading ? (
                <div className="p-10 flex justify-center">
                  <Spinner />
                </div>
              ) : predictionList.length === 0 ? (
                <div className="p-10 text-center text-gray-500">
                  No predictions found.
                </div>
              ) : (
                predictionList.map((row: any) => {
                  // Inside cricket_match = guessed, Outside = correct
                  const isTossCorrect =
                    row.cricket_match?.toss_winner?.id === row.toss_winner?.id;
                  const isMatchCorrect =
                    row.cricket_match?.match_winner?.id ===
                    row.match_winner?.id;
                  const isPOMCorrect =
                    row.cricket_match?.player_of_the_match?.id ===
                    row.player_of_the_match?.id;

                  return (
                    <div
                      key={row.id}
                      className="grid grid-cols-6 p-4 text-sm items-center"
                    >
                      <div className="text-center font-medium text-gray-600">
                        {row?.cricket_match?.id || ""}
                      </div>
                      <div className="text-center font-bold text-gray-800">
                        {userData.display_name}
                      </div>

                      {/* Toss Winner */}
                      <div className="text-center space-y-1">
                        <p
                          className={`font-bold ${isTossCorrect ? "text-[#1DA077]" : "text-[#E04F5F]"}`}
                        >
                          {row?.toss_winner?.name || "N/A"}
                        </p>
                        <p
                          className={`text-[10px] font-medium ${isTossCorrect ? "text-[#1DA077]" : "text-[#E04F5F]"}`}
                        >
                          +{row.toss_winner_point || 0} points
                        </p>
                      </div>

                      {/* Match Winner */}
                      <div className="text-center space-y-1">
                        <p
                          className={`font-bold ${isMatchCorrect ? "text-[#1DA077]" : "text-[#E04F5F]"}`}
                        >
                          {row?.match_winner?.name || "N/A"}
                        </p>
                        <p
                          className={`text-[10px] font-medium ${isMatchCorrect ? "text-[#1DA077]" : "text-[#E04F5F]"}`}
                        >
                          +{row.match_winner_point || 0} Points
                        </p>
                      </div>

                      {/* POM */}
                      <div className="text-center space-y-1">
                        <p
                          className={`font-bold ${isPOMCorrect ? "text-[#1DA077]" : "text-[#E04F5F]"}`}
                        >
                          {row?.player_of_the_match?.name ||
                            "N/A"}
                        </p>
                        <p
                          className={`text-[10px] font-medium ${isPOMCorrect ? "text-[#1DA077]" : "text-[#E04F5F]"}`}
                        >
                          +{row.player_of_the_match_point || 0} Points
                        </p>
                      </div>

                      {/* Per Match Total */}
                      <div className="text-center font-bold text-[#1DA077]">
                        +{row.total_points || 0} Points
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserPointsModal;
